import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Users } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact_person: "", contact_email: "", phone: "" };

export default function Clients() {
  const { user } = useAuth();
  const { brandTitle } = useBranding();
  const canEdit = ["ADMIN", "MANAGER"].includes(user?.role);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/clients/${editing}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Client updated" : "Client created");
      setOpen(false); setForm(empty); setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, contact_person: c.contact_person || "", contact_email: c.contact_email || "", phone: c.phone || "" });
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground mt-1">The end customers of {brandTitle}.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-client-button"><Plus className="h-4 w-4 mr-1" /> New Client</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Client</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" /></div>
                <div className="space-y-1"><Label>Contact Person</Label>
                  <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="client-person-input" /></div>
                <div className="space-y-1"><Label>Email</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-email-input" /></div>
                <div className="space-y-1"><Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" /></div>
              </div>
              <DialogFooter><Button onClick={save} disabled={!form.name} data-testid="save-client-button">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {clients.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-muted-foreground">
          <Users className="h-8 w-8 mx-auto mb-2 opacity-40" /> No clients yet.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <Card key={c.id} className={!c.is_active ? "opacity-50" : ""} data-testid={`client-card-${c.id}`}>
              <CardContent className="p-5 space-y-1">
                <div className="flex justify-between items-start">
                  <div className="font-heading font-semibold">{c.name}</div>
                  {canEdit && <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>Edit</Button>}
                </div>
                {c.contact_person && <div className="text-sm">{c.contact_person}</div>}
                {c.contact_email && <div className="text-sm text-muted-foreground">{c.contact_email}</div>}
                {c.phone && <div className="text-sm text-muted-foreground">{c.phone}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
